import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { decodeRoles, hasCockpitAccess } from '../../lib/auth'
import {
  canSeeDispatch,
  fetchActiveRuns,
  fetchDrivers,
  fetchReadyToSend,
  reassignRun,
  sendOut,
  type ActiveRun,
  type DispatchDriver,
  type ReadyToSendOrder,
} from '../../lib/dispatch-api'
import type { Lang } from '../../lib/i18n'
import { formatOrderId } from '../../lib/orders-api'
import { CommandHeader } from '../../components/CommandHeader'
import { dt } from './dispatch-i18n'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

type AuthState = 'checking' | 'ok' | 'redirect'
type LoadState = 'loading' | 'ready' | 'error'
type SendState = { kind: 'idle' } | { kind: 'pending'; driverId: string }

function displayCode(o: { id: string; shortCode: string | null }): string {
  return formatOrderId(o.id, o.shortCode)
}

// R4 — Bilal steps in for Nour: the same "send out" lists and tap as the cashier app's
// SendOutScreen, plus "On the road" (fetchActiveRuns) with a reassign lever. Every
// string comes from dispatch-i18n.ts.
export function DispatchScreen({ lang, onLangToggle, isOnline }: Props) {
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [orders, setOrders] = useState<ReadyToSendOrder[]>([])
  const [drivers, setDrivers] = useState<DispatchDriver[]>([])
  const [activeRuns, setActiveRuns] = useState<ActiveRun[]>([])
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [sendState, setSendState] = useState<SendState>({ kind: 'idle' })
  const [statusNotice, setStatusNotice] = useState<string | null>(null)
  const [alertMessage, setAlertMessage] = useState<string | null>(null)
  const [openReassignRunId, setOpenReassignRunId] = useState<string | null>(null)
  const [reassignError, setReassignError] = useState<Record<string, string | null>>({})
  const [reassignPendingDriverId, setReassignPendingDriverId] = useState<string | null>(null)

  useEffect(() => {
    hasCockpitAccess().then((ok) => setAuthState(ok && canSeeDispatch(decodeRoles()) ? 'ok' : 'redirect'))
  }, [])

  const load = useCallback(() => {
    setLoadState('loading')
    Promise.all([fetchReadyToSend(), fetchDrivers(), fetchActiveRuns()])
      .then(([readyOrders, readyDrivers, runs]) => {
        setOrders(readyOrders)
        setDrivers(readyDrivers)
        setActiveRuns(runs)
        setSelected(new Set())
        setLoadState('ready')
      })
      .catch(() => {
        setLoadState('error')
      })
  }, [])

  useEffect(() => {
    if (authState === 'ok') load()
  }, [authState, load])

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  function toggleOrder(orderId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  async function handleTapDriver(driver: DispatchDriver) {
    if (driver.busy || selected.size === 0 || sendState.kind === 'pending') return
    const orderIds = [...selected]
    setAlertMessage(null)
    setSendState({ kind: 'pending', driverId: driver.id })
    try {
      await sendOut(orderIds, driver.id)
      setOrders((prev) => prev.filter((o) => !orderIds.includes(o.id)))
      setSelected(new Set())
      setStatusNotice(`${dt(lang, 'sentTo')} ${driver.name}`)
      setSendState({ kind: 'idle' })
    } catch (err) {
      setAlertMessage(err instanceof Error ? err.message : dt(lang, 'loadError'))
      setSendState({ kind: 'idle' })
    }
  }

  function openReassign(runId: string) {
    setOpenReassignRunId(runId)
    setReassignError((prev) => ({ ...prev, [runId]: null }))
  }

  async function handleReassign(run: ActiveRun, driver: DispatchDriver) {
    if (driver.busy) return
    setReassignPendingDriverId(driver.id)
    setReassignError((prev) => ({ ...prev, [run.id]: null }))
    try {
      await reassignRun(run.id, driver.id)
      setOpenReassignRunId(null)
      setReassignPendingDriverId(null)
      load()
    } catch (err) {
      setReassignPendingDriverId(null)
      setReassignError((prev) => ({
        ...prev,
        [run.id]: err instanceof Error ? err.message : dt(lang, 'loadError'),
      }))
    }
  }

  return (
    <div dir={dir} className="min-h-screen bg-gray-50 font-sans">
      <CommandHeader title={dt(lang, 'title')} lang={lang} onLangToggle={onLangToggle} isOnline={isOnline} />

      <main className="px-4 py-6 max-w-2xl mx-auto pb-10 space-y-8">
        {loadState === 'loading' && <p className="text-sm text-gray-400 py-8 text-center">…</p>}

        {loadState === 'error' && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
            <span>{dt(lang, 'loadError')}</span>
            <button type="button" onClick={load} className="font-semibold underline">
              {dt(lang, 'retry')}
            </button>
          </div>
        )}

        {loadState === 'ready' && (
          <>
            <div className="flex items-center justify-end">
              <button
                type="button"
                data-testid="dispatch-refresh"
                onClick={load}
                className="text-sm font-semibold text-gray-700 hover:text-gray-900 min-h-[44px]"
              >
                {dt(lang, 'refresh')}
              </button>
            </div>

            {statusNotice && (
              <p
                role="status"
                data-testid="dispatch-status-notice"
                className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700"
              >
                {statusNotice}
              </p>
            )}

            {alertMessage && (
              <div
                role="alert"
                data-testid="dispatch-alert"
                className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
              >
                {alertMessage}
              </div>
            )}

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{dt(lang, 'sendOut')}</h2>
              {orders.length === 0 && <p className="text-sm text-gray-400">{dt(lang, 'readyEmpty')}</p>}
              {orders.length > 0 && (
                <ul className="space-y-2 mb-4" data-testid="dispatch-ready-list">
                  {orders.map((order) => (
                    <li
                      key={order.id}
                      data-testid={`dispatch-ready-row-${order.id}`}
                      className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-3 py-2"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(order.id)}
                        onChange={() => toggleOrder(order.id)}
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{displayCode(order)}</p>
                        <p className="text-xs text-gray-500">
                          {order.customerName ?? '—'}
                          {order.deliveryZoneName ? ` · ${order.deliveryZoneName}` : ''}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {drivers.length === 0 && <p className="text-sm text-gray-400">{dt(lang, 'driversEmpty')}</p>}
              {drivers.length > 0 && (
                <ul className="flex flex-wrap gap-2" data-testid="dispatch-driver-list">
                  {drivers.map((driver) => {
                    const isPendingThis = sendState.kind === 'pending' && sendState.driverId === driver.id
                    const disabled = driver.busy || selected.size === 0 || sendState.kind === 'pending'
                    return (
                      <li key={driver.id}>
                        <button
                          type="button"
                          data-testid={`dispatch-driver-${driver.id}`}
                          disabled={disabled}
                          onClick={() => void handleTapDriver(driver)}
                          className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-50 transition-colors min-h-[44px]"
                        >
                          {isPendingThis
                            ? dt(lang, 'sending')
                            : driver.busy
                              ? `${driver.name} — ${dt(lang, 'onRun')}`
                              : driver.name}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{dt(lang, 'onTheRoad')}</h2>
              {activeRuns.length === 0 && <p className="text-sm text-gray-400">{dt(lang, 'noActiveRuns')}</p>}
              {activeRuns.length > 0 && (
                <ul className="space-y-3" data-testid="dispatch-active-runs">
                  {activeRuns.map((run) => (
                    <li
                      key={run.id}
                      data-testid={`dispatch-run-${run.id}`}
                      className="bg-white rounded-lg border border-gray-200 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{run.driver?.name ?? '—'}</p>
                          <p className="text-xs text-gray-500">
                            {run.stops.map((s) => displayCode({ id: s.orderId, shortCode: s.shortCode })).join(', ')}{' '}
                            · {run.status}
                          </p>
                        </div>
                        <button
                          type="button"
                          data-testid={`dispatch-reassign-btn-${run.id}`}
                          onClick={() => openReassign(run.id)}
                          className="text-sm font-semibold text-gray-700 hover:text-gray-900 min-h-[44px]"
                        >
                          {dt(lang, 'reassign')}
                        </button>
                      </div>

                      {reassignError[run.id] && (
                        <div
                          role="alert"
                          data-testid={`dispatch-reassign-error-${run.id}`}
                          className="mt-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700"
                        >
                          {reassignError[run.id]}
                        </div>
                      )}

                      {openReassignRunId === run.id && (
                        <ul className="mt-3 flex flex-wrap gap-2" data-testid={`dispatch-reassign-list-${run.id}`}>
                          {/* "opens the free drivers" (§1.8) — a busy driver is not offered here at all. */}
                          {drivers
                            .filter((driver) => !driver.busy)
                            .map((driver) => (
                              <li key={driver.id}>
                                <button
                                  type="button"
                                  data-testid={`dispatch-reassign-driver-${run.id}-${driver.id}`}
                                  disabled={reassignPendingDriverId !== null}
                                  onClick={() => void handleReassign(run, driver)}
                                  className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-50 transition-colors min-h-[44px]"
                                >
                                  {reassignPendingDriverId === driver.id ? dt(lang, 'sending') : driver.name}
                                </button>
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  )
}
